import { useState, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { Table, Pagination, MobileTable } from '../../../../components';
import { Button, Box, useMediaQuery, Link, Tooltip } from '@mui/material';
import { CloudDownload, Send, Visibility } from '@mui/icons-material';
import { getDocuments, exportDocumentsCSV } from '../../../../fetch';
import { downloadFile, formatDate, getDownloadFileName } from '../../../../helpers';
import { IDocument } from '../../../../models';
import { useQuery } from 'react-query';
import { PendingContractsFilters } from './PendingContractsFilters';
import { LoadingButton } from '@mui/lab';
import { SendReminderModal } from './SendReminderModal';

export const PendingContracts = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [search, setSearch] = useState<string>('');
  const [startSentAt, setStartSentAt] = useState<string | null>(null);
  const [endSentAt, setEndSentAt] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [selectedSort, setSelectedSort] = useState<string>('sentDate');
  const [sortDirection, setSortDirection] = useState<{
    documentName?: 'Asc' | 'Desc';
    vendorName?: 'Asc' | 'Desc';
    personFirstName?: 'Asc' | 'Desc';
    sentDate?: 'Asc' | 'Desc';
    recipientSignedAt?: 'Asc' | 'Desc';
    corporateSignedAt?: 'Asc' | 'Desc';
  }>({
    sentDate: 'Desc'
  });
  const [isDownLoadingCsv, setIsDownLoadingCsv] = useState<boolean>(false);
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [sendReminderDocumentId, setSendReminderDocumentId] = useState<string | null>(null);

  const { data, isLoading } = useQuery(
    [
      'pendingContracts',
      page,
      perPage,
      selectedSort,
      sortDirection,
      search,
      startSentAt,
      endSentAt
    ],
    () =>
      getDocuments({
        page: page + 1,
        perPage: perPage,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string,
        statuses: [
          'Uploaded',
          'Sent',
          'Draft',
          'Viewed',
          'WaitingApproval',
          'Approved',
          'WaitingPay',
          'Paid'
        ],
        search,
        startSentAt,
        endSentAt
      }),
    {
      onSuccess: res => {
        setRecordCount(res.totalRecordCount);
      }
    }
  );

  const handleClickColumn = (column: string) => {
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
    });
  };

  const handleView = (documentLink: string) => {
    window.open(documentLink, '_blank');
  };

  const columns = useMemo(() => {
    return [
      {
        Header: 'Name',
        accessor: 'documentName',
        isServerSorted: selectedSort === 'documentName',
        isServerSortedDesc: sortDirection.documentName === 'Desc',
        handleClickColumn: () => handleClickColumn('documentName')
      },
      {
        Header: 'Vendor Name',
        accessor: 'vendorName',
        isServerSorted: selectedSort === 'vendorName',
        isServerSortedDesc: sortDirection.vendorName === 'Desc',
        handleClickColumn: () => handleClickColumn('vendorName'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          return <>{original?.partner?.name ?? ''}</>;
        }
      },
      {
        Header: 'Contract Manager Name',
        accessor: 'contractManagerName',
        isServerSorted: selectedSort === 'personFirstName',
        isServerSortedDesc: sortDirection.personFirstName === 'Desc',
        handleClickColumn: () => handleClickColumn('personFirstName'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          const { partner } = original;
          return (
            <>
              {partner?.contractsManager?.firstName && partner?.contractsManager?.lastName
                ? `${partner?.contractsManager?.firstName} ${partner?.contractsManager?.lastName}`
                : ''}
            </>
          );
        }
      },
      {
        Header: 'Date Sent',
        accessor: 'sentDate',
        isServerSorted: selectedSort === 'sentDate',
        isServerSortedDesc: sortDirection.sentDate === 'Desc',
        handleClickColumn: () => handleClickColumn('sentDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          const { sentAt } = original;
          return <>{sentAt ? formatDate(sentAt) : ''}</>;
        }
      },
      {
        Header: 'Vendor Signed',
        accessor: 'vendorSigned',
        isServerSorted: selectedSort === 'recipientSignedAt',
        isServerSortedDesc: sortDirection.recipientSignedAt === 'Desc',
        handleClickColumn: () => handleClickColumn('recipientSignedAt'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          const { hasRecipientSigned } = original;
          return <>{hasRecipientSigned ? 'Yes' : 'No'}</>;
        }
      },
      {
        Header: 'SMB Signed',
        accessor: 'smbSigned',
        isServerSorted: selectedSort === 'corporateSignedAt',
        isServerSortedDesc: sortDirection.corporateSignedAt === 'Desc',
        handleClickColumn: () => handleClickColumn('corporateSignedAt'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          const { hasCorporateSigned } = original;
          return <>{hasCorporateSigned ? 'Yes' : 'No'}</>;
        }
      },
      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IDocument } };
        }) => {
          return (
            <Box display="flex" gap={2} justifyContent="flex-end">
              <Tooltip
                title={
                  original.status === 'Draft' || original.status === 'Uploaded'
                    ? 'Document is in draft, reminder cannot be sent.'
                    : ''
                }
              >
                <span>
                  <Button
                    id="edit"
                    color="primary"
                    startIcon={<Send />}
                    disabled={original.status === 'Draft' || original.status === 'Uploaded'}
                    onClick={() => {
                      setSendReminderDocumentId(original.documentId);
                    }}
                  >
                    Send Reminder
                  </Button>
                </span>
              </Tooltip>
              <Button
                id="view"
                color="primary"
                LinkComponent={Link}
                href={original.pandDocUri}
                target="_blank"
                startIcon={<Visibility />}
              >
                View
              </Button>
            </Box>
          );
        }
      }
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.records]);
  return (
    <>
      <Box mt={1}>
        <PendingContractsFilters
          applyFilters={({
            startSentAt: newStartSentAt,
            endSentAt: newEndSentAt,
            search: newSearch
          }) => {
            setSearch(newSearch ?? '');
            setStartSentAt(newStartSentAt ?? null);
            setEndSentAt(newEndSentAt ?? null);
            setPage(0);
          }}
        />
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <LoadingButton
            loading={isDownLoadingCsv}
            loadingPosition="start"
            color="primary"
            variant="contained"
            disabled={isLoading ? true : false}
            startIcon={<CloudDownload />}
            onClick={async () => {
              setIsDownLoadingCsv(true);
              try {
                const res = await exportDocumentsCSV({
                  sort: selectedSort,
                  sortDirection: sortDirection[selectedSort] as string,
                  statuses: [
                    'Uploaded',
                    'Sent',
                    'Draft',
                    'Viewed',
                    'WaitingApproval',
                    'Approved',
                    'WaitingPay',
                    'Paid'
                  ],
                  search,
                  startSentAt,
                  endSentAt
                });
                const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8,' });
                downloadFile(blob, getDownloadFileName(res));
              } catch (error) {
                enqueueSnackbar(`Error, unable to download file, please try again.`, {
                  variant: 'error'
                });
              } finally {
                setIsDownLoadingCsv(false);
              }
            }}
          >
            CSV
          </LoadingButton>
        </Box>
      </Box>
      <Table
        columns={columns}
        data={data?.records ?? []}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            })),
          handleView: (val: IDocument) => {
            handleView(val.pandDocUri ?? '');
          },
          sendReminder: (val: IDocument) => {
            if (val.status !== 'Draft' && val.status !== 'Uploaded') {
              setSendReminderDocumentId(val.documentId);
            }
          }
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setRowsPerPage}
        />
      )}
      <SendReminderModal
        isOpen={!!sendReminderDocumentId}
        pandaDocId={sendReminderDocumentId ?? ''}
        onClose={() => setSendReminderDocumentId(null)}
      />
    </>
  );
};
